import React, { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, Loader2, Eye, EyeOff, AlertCircle } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = useMemo(() => params.get("token") || "", [params]);

  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errMsg, setErrMsg] = useState("");

  const tokenMissing = !token;

  const submit = async (e) => {
    e.preventDefault();
    setErrMsg("");
    if (!password || !passwordConfirm) {
      toast.error("Please fill in both password fields.");
      return;
    }
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    if (password !== passwordConfirm) {
      toast.error("Passwords do not match.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      navigate("/account?msg=password-reset", { replace: true });
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "This reset link has expired or is invalid. Please request a new one.";
      setErrMsg(String(msg));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative min-h-[80vh] bg-[#0A1628] text-[#F8FAFC] flex items-center justify-center py-20 px-4">
      <div className="absolute inset-0 so-grid opacity-25 pointer-events-none" />
      <div className="absolute -top-20 -right-20 w-[420px] h-[420px] so-glow-blue" />
      <div className="absolute bottom-0 left-0 w-[420px] h-[420px] so-glow-cyan" />

      {tokenMissing || errMsg ? (
        <div
          data-testid="reset-error-card"
          className="relative so-card rounded-2xl p-8 w-full max-w-md"
        >
          <div className="inline-flex items-center gap-2 uppercase text-[11px] tracking-widest text-[#EC4899] font-mono">
            <AlertCircle size={12} /> Reset link issue
          </div>
          <h1 className="mt-3 font-display text-2xl font-extrabold tracking-tight">
            Link expired or invalid
          </h1>
          <p className="mt-3 text-[#94A3B8] text-sm leading-relaxed">
            {tokenMissing
              ? "This reset link is missing its token. Please request a new one from the sign in page."
              : errMsg}
          </p>
          <Link
            to="/account"
            data-testid="reset-error-back-link"
            className="mt-6 btn-blue w-full inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-medium"
          >
            Request a new link <ArrowRight size={14} />
          </Link>
        </div>
      ) : (
        <form
          onSubmit={submit}
          data-testid="reset-password-form"
          className="relative so-card rounded-2xl p-8 w-full max-w-md"
        >
          <div className="uppercase text-[11px] tracking-widest text-[#06B6D4] font-mono">
            Account
          </div>
          <h1 className="mt-3 font-display text-2xl font-extrabold tracking-tight">
            Choose a new password
          </h1>
          <p className="mt-2 text-[#94A3B8] text-sm">
            Pick something at least 8 characters long. You will be signed in with the new
            password after this.
          </p>

          <div className="mt-6 space-y-5">
            <div>
              <label className="block text-xs uppercase tracking-widest text-[#94A3B8] mb-2 font-mono">
                New password
              </label>
              <div className="relative">
                <input
                  data-testid="reset-new-password"
                  type={showPw ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  className="so-input w-full rounded-lg px-4 py-3 text-sm pr-10"
                  autoComplete="new-password"
                  minLength={8}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPw((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-[#F8FAFC]"
                  aria-label="Toggle password visibility"
                >
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-xs uppercase tracking-widest text-[#94A3B8] mb-2 font-mono">
                Confirm new password
              </label>
              <input
                data-testid="reset-password-confirm"
                type={showPw ? "text" : "password"}
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
                placeholder="Re-enter your new password"
                className="so-input w-full rounded-lg px-4 py-3 text-sm"
                autoComplete="new-password"
                minLength={8}
                required
              />
              {passwordConfirm && password !== passwordConfirm && (
                <div
                  data-testid="reset-password-mismatch"
                  className="mt-2 text-xs text-[#EC4899]"
                >
                  Passwords don&apos;t match yet.
                </div>
              )}
            </div>

            <button
              type="submit"
              disabled={submitting}
              data-testid="reset-submit-btn"
              className="btn-blue w-full inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-medium disabled:opacity-60"
            >
              {submitting ? (
                <>
                  <Loader2 size={14} className="animate-spin" /> Updating password…
                </>
              ) : (
                <>
                  Update password <ArrowRight size={14} />
                </>
              )}
            </button>

            <Link
              to="/account"
              data-testid="reset-back-to-signin"
              className="block w-full text-center text-sm text-[#06B6D4] hover:underline"
            >
              Back to sign in
            </Link>
          </div>
        </form>
      )}
    </div>
  );
}
